import {
  BodyText,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import type { Practitioner } from "@curai/js-fhir-tools";
import { AnimatePresence } from "motion/react";
import { useEffect, useState } from "react";
import { LiveQueueRow } from "./LiveQueueRow";
import type { WaitingRoomItem } from "./network/useWaitingRoomDashboard";
import type { FreezeHandlers } from "./useFrozenQueue";

interface LiveQueueTableProps {
  myQueue: WaitingRoomItem[] | undefined;
  otherCliniciansQueue: WaitingRoomItem[] | undefined;
  isError?: boolean;
  isLoading?: boolean;
  practitioner: Practitioner;
  mineOnly: boolean;
  onMineOnlyChange: (mineOnly: boolean) => void;
  abridged: boolean;
  onAbridgedChange: (abridged: boolean) => void;
  /** Patients that arrived/left while the list was frozen (see useFrozenQueue). */
  addedCount: number;
  removedCount: number;
  isFrozen: boolean;
  onFlush: () => void;
  freezeHandlers: FreezeHandlers;
  animationsEnabled: boolean;
}

export const LiveQueueTable = ({
  myQueue,
  otherCliniciansQueue,
  isLoading,
  isError,
  practitioner,
  mineOnly,
  onMineOnlyChange,
  abridged,
  onAbridgedChange,
  addedCount,
  removedCount,
  isFrozen,
  onFlush,
  freezeHandlers,
  animationsEnabled,
}: LiveQueueTableProps) => {
  const showOtherClinicians = !mineOnly;
  const hasMyQueue = (myQueue?.length ?? 0) > 0;
  const columnCount = 6;

  // Only flash newly-inserted rows green *after* the initial render, so the
  // whole queue doesn't tint green on first paint when the flag is already on.
  const [hasRendered, setHasRendered] = useState(false);
  useEffect(() => setHasRendered(true), []);
  const flashOnMount = animationsEnabled && hasRendered;

  // While frozen, tell the clinician what will change on settle: "+N / −N".
  const pendingParts = [
    addedCount > 0 ? `+${addedCount}` : null,
    removedCount > 0 ? `−${removedCount}` : null,
  ].filter(Boolean);
  const pendingLabel = `${addedCount} arriving, ${removedCount} leaving — click to update the queue`;

  return (
    // The freeze handlers span the whole list region: hovering, focusing, or
    // pressing anywhere here pauses realtime reordering until the pointer
    // leaves (or the max-freeze cap fires).
    <div
      className="relative"
      data-frozen={isFrozen || undefined}
      {...freezeHandlers}
    >
      {pendingParts.length > 0 && (
        <div
          className="absolute inset-x-0 top-2 z-10 flex justify-center pointer-events-none"
          aria-live="polite"
        >
          <Button
            variant="secondary"
            size="sm"
            data-testid="live-queue-new-patients-pill"
            aria-label={pendingLabel}
            className="pointer-events-auto rounded-full shadow-md"
            onClick={onFlush}
          >
            {pendingParts.join(" / ")} patient
            {addedCount + removedCount > 1 ? "s" : ""} ↑
          </Button>
        </div>
      )}
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-64 whitespace-nowrap">
              <BodyText bold={true} size="sm" textColor="tertiary">
                Name
              </BodyText>
            </TableHead>
            <TableHead className="w-32 whitespace-nowrap">
              <BodyText bold={true} size="sm" textColor="tertiary">
                Age & Sex
              </BodyText>
            </TableHead>
            <TableHead className="w-28 whitespace-nowrap">
              <BodyText bold={true} size="sm" textColor="tertiary">
                State
              </BodyText>
            </TableHead>
            <TableHead className="w-32 whitespace-nowrap">
              <BodyText bold={true} size="sm" textColor="tertiary">
                Partner
              </BodyText>
            </TableHead>
            <TableHead className="w-64 whitespace-nowrap">
              <BodyText bold={true} size="sm" textColor="tertiary">
                Chief Complaint
              </BodyText>
            </TableHead>
            <TableHead className="w-48">
              <span />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody
          columnCount={columnCount}
          isLoading={isLoading}
          isError={isError}
        >
          <AnimatePresence initial={false}>
            {myQueue?.map((message) => (
              <LiveQueueRow
                key={message.encounter.id}
                message={message}
                animate={animationsEnabled}
                flashOnMount={flashOnMount}
              />
            ))}
          </AnimatePresence>
          {!hasMyQueue && (
            <TableRow>
              <TableCell colSpan={columnCount} className="text-center">
                <BodyText bold={true} size="sm">
                  No Assigned Patients
                </BodyText>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell colSpan={columnCount} className="text-left">
              <div className="flex gap-6 items-end">
                {practitioner.isLicensedClinician && (
                  <div className="flex gap-2 items-end">
                    <Switch
                      data-testid="show-all-clinicians-toggle"
                      onClick={() => {
                        trackEvent(TRACKERS.TOGGLE_SHOW_ALL_CLINICIANS_ON_OFF);
                        onMineOnlyChange(!mineOnly);
                      }}
                      checked={showOtherClinicians}
                    />
                    <BodyText size="sm" textColor="success">
                      Show All Clinicians
                    </BodyText>
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <Switch
                    data-testid="show-all-encounters-toggle"
                    onClick={() => onAbridgedChange(!abridged)}
                    checked={!abridged}
                  />
                  <BodyText size="sm" textColor="success">
                    Show All Encounters
                  </BodyText>
                </div>
              </div>
            </TableCell>
          </TableRow>
          {showOtherClinicians && (
            <TableRow>
              <TableCell colSpan={columnCount} className="text-left">
                <BodyText size="md">Other Clinicians</BodyText>
              </TableCell>
            </TableRow>
          )}
          <AnimatePresence initial={false}>
            {showOtherClinicians &&
              otherCliniciansQueue?.map((message) => (
                <LiveQueueRow
                  key={message.encounter.id}
                  message={message}
                  animate={animationsEnabled}
                  flashOnMount={flashOnMount}
                />
              ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
};
